import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAccountSetup } from "../../../contexts/AccountSetupContext";
import { useProfileSetup } from "../../../contexts/ProfileContext";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import DownloadReportButton from "../../buttons/DownloadReportButton";
import MetricPalette from "./MetricPalette";
import ReportCanvas from "./ReportCanvas";
import { CATALOG_BY_ID } from "./reportsCatalog";
import { fetchWidgetData } from "./reportsData";
import { DATE_PRESETS, DEFAULT_PRESET_ID, getPreset, rangeLabel } from "./dateRanges";
import { fetchReportLayout, saveReportLayout } from "./reportLayoutApi";

const SAVE_DEBOUNCE_MS = 800;

// A widget that fails transiently (e.g. an intermittent backend 503 from the
// concurrent request burst) is never shown as an error. It stays on its
// LoadingSpinner and is retried in the background — sequentially, one metric at
// a time, with capped exponential backoff — until it returns data. The retry is
// gentle (one at a time, backing off) so it never recreates the burst that
// caused the failure. Only a deterministic "disconnected channel" stops the
// retries; that renders an actionable reconnect prompt instead of a raw error.
const RETRY_BASE_MS = 500;
const RETRY_MAX_MS = 8000;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The set of widgets a user sees the first time they open Reports (before they
// customize their own). Order is preserved. Any widget whose channel is not
// connected is skipped, and the default set is not persisted until the user
// makes their first change.
const DEFAULT_WIDGET_CATALOG_IDS = [
  "ga.sessions",
  "ga.total_users",
  "ga.new_users",
  "ga.engagement_rate",
  "ga.top_regions",
  "ga.top_pages",
  "gads.spend",
  "meta.spend",
  "gads.impressions",
  "meta.impressions",
  "gads.clicks",
  "meta.clicks",
  "gads.conversions",
];

const SIZE_ORDER = ["sm", "md", "lg"];

function defaultSize(type) {
  if (type === "kpi") return "sm";
  if (type === "breakdown") return "md";
  return "lg";
}

function makeInstanceId(catalogId) {
  return `${catalogId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function buildInstance(entry, { hasUser, size } = {}) {
  return {
    instanceId: makeInstanceId(entry.id),
    catalogId: entry.id,
    channel: entry.channel,
    type: entry.type,
    label: entry.label,
    unit: entry.unit || "",
    size: SIZE_ORDER.includes(size) ? size : defaultSize(entry.type),
    status: hasUser ? "loading" : "error",
    error: hasUser ? null : "Sign in to load data",
    data: null,
  };
}

// A widget's data request fails as "channel disconnected" when the backend has
// no stored connection/account for that channel (the user removed it). We match
// the backend's "No <Channel> connection found / ad account connected" messages
// so those widgets show a friendly reconnect prompt instead of a raw error.
function isDisconnectedError(message) {
  return /no .*(connection|account) (found|connected)|not connected/i.test(String(message || ""));
}

export default function ReportsView() {
  const {
    userId,
    connections,
    loading: connectionsLoading,
    initialized: connectionsInitialized,
  } = useAccountSetup();
  const { profileData, getAuthHeaders, apiBase } = useProfileSetup();

  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const [placed, setPlaced] = useState([]);
  const [reportTitle, setReportTitle] = useState("Marketing Report");
  const [subtitle, setSubtitle] = useState("");
  const [downloadError, setDownloadError] = useState(null);
  const [metricsOpen, setMetricsOpen] = useState(true);

  const preset = useMemo(() => getPreset(presetId), [presetId]);
  const { startDate, endDate } = useMemo(() => preset.compute(), [preset]);

  const placedRef = useRef(placed);
  const ctxRef = useRef({ userId, startDate, endDate });
  // Monotonic id bumped on every full (re)load. A sequential retry sweep
  // captures the id it started under and bails the moment a newer load
  // supersedes it, so stale retries can't fight a fresh reload.
  const loadGenRef = useRef(0);
  // Guards a hydration request that is currently in flight so we never launch
  // two backend loads at once (e.g. under StrictMode's double-invoked effects).
  const hydratingRef = useRef(false);
  // Set true once the first hydration attempt settles; gates the save effect so
  // the seeded/restored layout isn't persisted before the user customizes it.
  const hydratedRef = useRef(false);
  // Becomes true only when the user actually changes their report, so the
  // default/seed layout (and freshly-restored layouts) are never auto-persisted.
  const dirtyRef = useRef(false);
  const getAuthHeadersRef = useRef(getAuthHeaders);
  // Latest channel-connection status, read (not depended on) during hydration so
  // restoring a saved report never has to wait on — or re-run because of — the
  // connections context. Only the first-time default seed consults it.
  const connectionsRef = useRef(connections);
  useEffect(() => {
    placedRef.current = placed;
  }, [placed]);
  useEffect(() => {
    getAuthHeadersRef.current = getAuthHeaders;
  }, [getAuthHeaders]);
  useEffect(() => {
    connectionsRef.current = connections;
  }, [connections]);
  // Bump the load id on unmount so any in-flight background retry loop bails
  // instead of continuing to fetch after the view is gone.
  useEffect(() => () => { loadGenRef.current += 1; }, []);

  // Loads a single widget and reports the outcome so the batch coordinator can
  // tell success from a transient failure and from a deterministic
  // "disconnected" channel. Never throws.
  //
  // On a transient failure we deliberately keep the widget in the "loading"
  // state (spinner stays up) rather than flipping it to "error" — the user
  // should only ever see data or a spinner, never a raw error like
  // "Request failed with status code 503". The retry loop heals it in the
  // background. Only a disconnected channel becomes an "error" (reconnect prompt).
  const loadWidget = useCallback(async (instance) => {
    const entry = CATALOG_BY_ID[instance.catalogId];
    if (!entry) return { ok: false, disconnected: false };
    try {
      const data = await fetchWidgetData(entry, ctxRef.current);
      setPlaced((prev) =>
        prev.map((p) =>
          p.instanceId === instance.instanceId
            ? { ...p, status: "loaded", data, error: null, disconnected: false }
            : p
        )
      );
      return { ok: true, disconnected: false };
    } catch (err) {
      const resp = err?.response?.data;
      const msg = (resp && typeof resp.error === "string" && resp.error) || err?.message || "Failed to load";
      const disconnected = isDisconnectedError(msg);
      setPlaced((prev) =>
        prev.map((p) => {
          if (p.instanceId !== instance.instanceId) return p;
          return disconnected
            ? { ...p, status: "error", error: msg, disconnected: true, data: null }
            : { ...p, status: "loading", error: null, disconnected: false, data: null };
        })
      );
      return { ok: false, disconnected };
    }
  }, []);

  // Load a batch fast (all at once) and, once every widget has settled, keep
  // retrying only the ones that failed transiently — sequentially, one metric at
  // a time, with capped exponential backoff — until they all return data. Failed
  // widgets stay on their spinners the whole time (see loadWidget), so the user
  // never sees a raw error. This keeps the happy path snappy while healing the
  // intermittent 503s without firing another concurrent burst. `gen` is the load
  // id from the caller; the sweep bails the moment a newer reload supersedes it.
  const loadBatchWithRetry = useCallback(
    async (instances, gen) => {
      if (!instances.length) return;

      // 1) Initial concurrent load (unchanged happy-path behavior).
      const results = await Promise.allSettled(instances.map(loadWidget));
      if (loadGenRef.current !== gen) return;

      // Only transient failures are worth retrying; skip disconnected channels.
      let pending = instances.filter((_, i) => {
        const outcome = results[i].status === "fulfilled" ? results[i].value : null;
        return outcome && !outcome.ok && !outcome.disconnected;
      });

      // 2) Sequential, backoff-capped retry sweeps over the stragglers, one
      //    metric at a time, until they all recover (or a newer load supersedes).
      for (let round = 0; pending.length; round += 1) {
        const delay = Math.min(RETRY_BASE_MS * 2 ** round, RETRY_MAX_MS);
        const stillPending = [];
        for (const instance of pending) {
          if (loadGenRef.current !== gen) return;
          await sleep(delay);
          if (loadGenRef.current !== gen) return;
          // Skip anything the user removed while we were retrying.
          if (!placedRef.current.some((p) => p.instanceId === instance.instanceId)) continue;
          const outcome = await loadWidget(instance);
          if (outcome && !outcome.ok && !outcome.disconnected) stillPending.push(instance);
        }
        pending = stillPending;
      }
    },
    [loadWidget]
  );

  const reloadAll = useCallback(() => {
    const list = placedRef.current;
    if (!list.length) return;
    const gen = (loadGenRef.current += 1);
    setPlaced((prev) => prev.map((p) => ({ ...p, status: "loading" })));
    loadBatchWithRetry(list, gen);
  }, [loadBatchWithRetry]);

  // Re-fetch every placed widget whenever the user or the date range changes,
  // so the on-screen data (and the snapshot sent to the PDF) stays in sync.
  useEffect(() => {
    ctxRef.current = { userId, startDate, endDate };
    if (!userId) return;
    reloadAll();
  }, [userId, startDate, endDate, reloadAll]);

  // Restore the saved report as soon as we know the user. Restoration keys off
  // `userId` alone — not the channel-connection context — because after a dev
  // hot-reload `userId` is reliably present while `connections` may still be
  // re-initializing. Gating on connections made restores racy: a load could run
  // while connections were still the default (all false), filter every saved
  // widget out, and then the corrected re-run would be swallowed by the in-flight
  // guard, leaving the canvas stuck empty. Saved widgets already carry their own
  // channel, so we restore them verbatim and only the first-time default seed
  // consults connection status.
  //
  // Also re-runs whenever the canvas ends up empty with no unsaved edits, so the
  // report comes back on its own after a hot reload clears React state (which
  // empties `placed` while keeping this component mounted).
  useEffect(() => {
    if (!userId) {
      hydratedRef.current = true;
      return;
    }

    // Leave a populated canvas untouched, never clobber unsaved user edits, and
    // never launch two loads at once.
    if (placed.length > 0 || dirtyRef.current || hydratingRef.current) return;
    hydratingRef.current = true;

    (async () => {
      try {
        let saved = null;
        try {
          const headers = await getAuthHeadersRef.current();
          saved = await fetchReportLayout({ apiBase, userId, headers });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Failed to load saved report layout", err);
        }

        if (saved) {
          if (saved.presetId && DATE_PRESETS.some((p) => p.id === saved.presetId)) {
            setPresetId(saved.presetId);
          }
          if (saved.reportTitle) setReportTitle(saved.reportTitle);
          if (typeof saved.subtitle === "string") setSubtitle(saved.subtitle);
        }

        let instances;
        if (saved) {
          // Returning user (even one who saved an empty canvas): restore exactly
          // what they saved, straight from the layout. No connection lookup, so
          // this is stable right after a hot reload regardless of context timing.
          instances = saved.widgets
            .map((w) => {
              const entry = CATALOG_BY_ID[w.catalogId];
              return entry ? buildInstance(entry, { hasUser: true, size: w.size }) : null;
            })
            .filter(Boolean);
        } else if (connectionsInitialized) {
          // First-time user with nothing saved: seed the default set, limited to
          // currently-connected channels. If connections aren't known yet we seed
          // nothing and let the `connectionsInitialized` re-run try again — there
          // is no saved layout to lose in the meantime.
          const conns = connectionsRef.current;
          instances = DEFAULT_WIDGET_CATALOG_IDS
            .map((catalogId) => {
              const entry = CATALOG_BY_ID[catalogId];
              if (!entry) return null;
              if (!conns || !conns[entry.channel]) return null;
              return buildInstance(entry, { hasUser: true });
            })
            .filter(Boolean);
        } else {
          instances = [];
        }

        if (instances.length) {
          setPlaced(instances);
          loadBatchWithRetry(instances, (loadGenRef.current += 1));
        }
      } finally {
        // Enable persistence only after the restore/seed has settled. The seeded
        // default set stays unpersisted (dirty stays false) until the user
        // changes it, so it can adapt to newly connected channels later.
        hydratedRef.current = true;
        hydratingRef.current = false;
      }
    })();
  }, [userId, apiBase, placed, loadBatchWithRetry, connectionsInitialized]);

  // Persist the layout (debounced) after the user changes their report. The
  // `dirtyRef` guard ensures the seeded default set and freshly-restored
  // layouts are not written back until the user actually customizes them.
  useEffect(() => {
    if (!hydratedRef.current || !userId || !dirtyRef.current) return;
    const handle = setTimeout(async () => {
      try {
        const headers = await getAuthHeadersRef.current();
        await saveReportLayout({
          apiBase,
          userId,
          headers,
          widgets: placedRef.current,
          presetId,
          reportTitle,
          subtitle,
        });
        dirtyRef.current = false;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to save report layout", err);
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [placed, presetId, reportTitle, subtitle, userId, apiBase]);

  const addWidget = useCallback(
    (catalogId, atIndex) => {
      const entry = CATALOG_BY_ID[catalogId];
      if (!entry) return;
      dirtyRef.current = true;
      const hasUser = !!ctxRef.current.userId;
      const instance = buildInstance(entry, { hasUser });
      setPlaced((prev) => {
        const next = [...prev];
        const idx = atIndex == null || atIndex > next.length ? next.length : atIndex;
        next.splice(idx, 0, instance);
        return next;
      });
      // Load (and self-heal) the new widget through the same retry path so a
      // transient failure spins + retries instead of surfacing an error. Reuse
      // the current load id (don't start a new one) so an in-flight batch sweep
      // for the other widgets keeps running.
      if (hasUser) loadBatchWithRetry([instance], loadGenRef.current);
    },
    [loadBatchWithRetry]
  );

  const removeWidget = useCallback((instanceId) => {
    dirtyRef.current = true;
    setPlaced((prev) => prev.filter((p) => p.instanceId !== instanceId));
  }, []);

  const removeWidgetsByCatalogId = useCallback((catalogId) => {
    dirtyRef.current = true;
    setPlaced((prev) => prev.filter((p) => p.catalogId !== catalogId));
  }, []);

  const resizeWidget = useCallback((instanceId) => {
    dirtyRef.current = true;
    setPlaced((prev) =>
      prev.map((p) =>
        p.instanceId === instanceId
          ? { ...p, size: SIZE_ORDER[(SIZE_ORDER.indexOf(p.size) + 1) % SIZE_ORDER.length] }
          : p
      )
    );
  }, []);

  const reorderWidget = useCallback((instanceId, toIndex) => {
    dirtyRef.current = true;
    setPlaced((prev) => {
      const from = prev.findIndex((p) => p.instanceId === instanceId);
      if (from === -1) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      let idx = from < toIndex ? toIndex - 1 : toIndex;
      if (idx < 0) idx = 0;
      if (idx > next.length) idx = next.length;
      next.splice(idx, 0, item);
      return next;
    });
  }, []);

  const snapshotWidgets = useMemo(
    () =>
      placed
        .filter((w) => w.status === "loaded" && w.data)
        .map((w) => ({
          id: w.catalogId,
          type: w.type,
          label: w.label,
          channel: w.channel,
          unit: w.unit,
          ...w.data,
        })),
    [placed]
  );

  const meta = useMemo(
    () => ({
      title: reportTitle.trim() || "Marketing Report",
      subtitle: subtitle.trim(),
      companyName: (profileData.companyName || "").trim(),
      dateRange: { startDate, endDate, label: rangeLabel(preset, startDate, endDate) },
    }),
    [reportTitle, subtitle, profileData.companyName, preset, startDate, endDate]
  );

  const placedCatalogIds = useMemo(() => placed.map((p) => p.catalogId), [placed]);

  if (connectionsLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Reports</h1>
          <p className="text-sm text-muted-foreground">
            Add metrics from your connected channels, then download a branded PDF.
          </p>
        </div>
        <DownloadReportButton
          widgets={snapshotWidgets}
          meta={meta}
          disabled={!snapshotWidgets.length}
          onError={(e) => setDownloadError(e?.message || "Download failed")}
        >
          Download Report{snapshotWidgets.length ? ` (${snapshotWidgets.length})` : ""}
        </DownloadReportButton>
      </div>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          Report title
          <input
            type="text"
            value={reportTitle}
            onChange={(e) => {
              dirtyRef.current = true;
              setReportTitle(e.target.value);
            }}
            className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          Subtitle (optional)
          <input
            type="text"
            value={subtitle}
            onChange={(e) => {
              dirtyRef.current = true;
              setSubtitle(e.target.value);
            }}
            placeholder="e.g. Q2 performance summary"
            className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          Date range
          <select
            value={presetId}
            onChange={(e) => {
              dirtyRef.current = true;
              setPresetId(e.target.value);
            }}
            className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {DATE_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {downloadError ? (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{downloadError}</span>
        </div>
      ) : null}

      <div className="flex flex-col gap-5 lg:flex-row">
        {metricsOpen ? (
          <aside className="lg:w-72 lg:shrink-0">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-foreground">Metrics</h2>
              <button
                type="button"
                onClick={() => setMetricsOpen(false)}
                title="Collapse metrics"
                aria-label="Collapse metrics sidebar"
                aria-expanded={true}
                className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>
            <MetricPalette
              connections={connections}
              onAdd={addWidget}
              onRemove={removeWidgetsByCatalogId}
              placedCatalogIds={placedCatalogIds}
            />
          </aside>
        ) : null}

        <section className="min-w-0 flex-1">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {metricsOpen ? null : (
                <button
                  type="button"
                  onClick={() => setMetricsOpen(true)}
                  title="Show metrics"
                  aria-label="Open metrics sidebar"
                  aria-expanded={false}
                  className="flex shrink-0 items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent"
                >
                  <PanelLeftOpen className="h-4 w-4" />
                  Metrics
                </button>
              )}
              <h2 className="truncate text-sm font-semibold text-foreground">Canvas</h2>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">
              {placed.length} widget{placed.length === 1 ? "" : "s"} &middot; {rangeLabel(preset, startDate, endDate)}
            </span>
          </div>
          <ReportCanvas
            widgets={placed}
            onAddMetric={addWidget}
            onRemove={removeWidget}
            onResize={resizeWidget}
            onReorder={reorderWidget}
          />
        </section>
      </div>
    </div>
  );
}